import { useCallback, useEffect, useRef } from 'react'

/**
 * Hook responsible for safe delays that respect component mounting state
 * Single Responsibility: Safe timeout management
 * @param isMounted - Function to check if the component is still mounted
 * @returns A function that returns a promise resolving to true if still mounted, false otherwise
 */
export const useSafeDelay = (isMounted: () => boolean) => {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    const safeDelay = useCallback(
        async (ms: number): Promise<boolean> => {
            return new Promise((resolve) => {
                timeoutRef.current = setTimeout(() => {
                    if (isMounted()) {
                        resolve(true)
                    } else {
                        resolve(false)
                    }
                }, ms)
            })
        },
        [isMounted]
    )

    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
        }
    }, [])

    return safeDelay
}
